import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import { push } from "react-router-redux";
import { AttendanceFilterValues, AttendanceListingPage, Attendee } from "../../models";

import ErrorSnackbar from "modules/common/components/snackbars/errorSnackbar";
import LoadingOverlay from "modules/common/components/loadingOverlay";

import BasePage from "pages/common/basePage";
import Breadcrumb from "pages/common/breadcrumb";
import MainContent from "pages/common/mainContent";
import Tabs from "pages/common/tabs";

import AttendanceList from "./attendanceList";

import IconButton from "@mui/material/IconButton";

import GetAppIcon from "@mui/icons-material/GetApp";
import { FileDownloader } from "utils/fileDownloader";

enum ATTENDANCE_LISTING_TAB {
    ATTENDING,
    NOT_ATTENDING,
    UNDECIDED,
    WAITLISTED,
    ALL
}

class AttendanceListing extends React.Component<PropsWithRedux, ComponentState> {
    constructor(props: PropsWithRedux) {
        super(props);

        this.state = {
            all: { ...this.getDefaultPageValues(), id: "all" },
            attending: { ...this.getDefaultPageValues(), id: "attending" },
            notAttending: { ...this.getDefaultPageValues(), id: "notAttending" },
            undecided: { ...this.getDefaultPageValues(), id: "undecided" },
            waitlist: { ...this.getDefaultPageValues(), id: "waitlist" },
            selectedItems: [],
            selectedTab: 0,
            showFilters: false
        };
    }

    public async componentDidMount(): Promise<void> {
        const eventId = this.props.match.params.eventId;
        if (!!eventId) {
            await this.fetchLists();
        }
    }

    public componentDidUpdate(prevProps: PropsWithRedux) {
        if ((!!this.props.match.params.eventId && (this.props.match.params.eventId !== prevProps.match.params.eventId)) ||
            (this.props.shouldFetch && (this.props.shouldFetch !== prevProps.shouldFetch))) {
            this.fetchLists();
        }
    }

    public render() {
        const { selectedTab } = this.state;

        return (
            <BasePage fullWidth>
                <Breadcrumb
                    items={[
                        { title: "Events", link: "~/admin/events" },
                        { title: this.getEventTitle() },
                        { title: "Manage participants" }
                    ]}
                    backItem={{ title: "Back to Manage Events", link: "~/admin/events" }}
                />
                <MainContent>
                    <div className="authoring-page">
                        <div>
                            <Tabs
                                tabs={[
                                    { label: this.getTabLabel("Attending", this.state.attending), id: "all-attendance-tab" },
                                    { label: this.getTabLabel("Not Attending", this.state.notAttending), id: "attending-attendance-tab" },
                                    { label: this.getTabLabel("Undecided", this.state.undecided), id: "not-attending-attendance-tab" },
                                    { label: this.getTabLabel("Waitlist", this.state.waitlist), id: "waitlist-attendance-tab" },
                                    { label: this.getTabLabel("All", this.state.all), id: "all-attendance-tab" },
                                ]}
                                tabOptions={
                                    <IconButton onClick={this.downloadAttendanceListing} size="large"><GetAppIcon /></IconButton>
                                }
                                selectedTab={selectedTab}
                                onSelectTab={this.onSelectTab}
                            />
                            <AttendanceList show={selectedTab === ATTENDANCE_LISTING_TAB.ATTENDING} eventId={this.props.match.params.eventId} page={this.state.attending} fetchPage={this.fetchAttending} />
                            <AttendanceList show={selectedTab === ATTENDANCE_LISTING_TAB.NOT_ATTENDING} eventId={this.props.match.params.eventId} page={this.state.notAttending} fetchPage={this.fetchNotAttending} />
                            <AttendanceList show={selectedTab === ATTENDANCE_LISTING_TAB.UNDECIDED} eventId={this.props.match.params.eventId} page={this.state.undecided} fetchPage={this.fetchUndecided} />
                            <AttendanceList show={selectedTab === ATTENDANCE_LISTING_TAB.WAITLISTED} eventId={this.props.match.params.eventId} page={this.state.waitlist} fetchPage={this.fetchWaitlist} />
                            <AttendanceList show={selectedTab === ATTENDANCE_LISTING_TAB.ALL} eventId={this.props.match.params.eventId} page={this.state.all} fetchPage={this.fetchAll} />
                        </div>
                        <ErrorSnackbar errorMessage={this.props.errorMessage} clearErrorMessage={this.props.clearErrorMessage} />
                        <LoadingOverlay absolute={true} show={this.props.isFetching} />
                    </div>
                </MainContent>
            </BasePage>
        );
    }

    private downloadAttendanceListing = () => {
        const file = {
            name: `participants-${new Date().toISOString()}.csv`
        };

        this.props.getAttendanceCSV(this.props.match.params.eventId)
            .then((data) => {
                if (!!data) new FileDownloader(file).downloadBlob(data)
            });
    }

    private fetchLists = async (): Promise<void> => {
        await Promise.all([
            this.fetchAll(1, {}),
            this.fetchAttending(1, {}),
            this.fetchNotAttending(1, {}),
            this.fetchUndecided(1, {}),
            this.fetchWaitlist(1, {})
        ]);
    }

    private fetchAll = async (pageNumber: number, filters: Partial<AttendanceFilterValues>): Promise<void> => {
        this.setState({ all: { ...this.getDefaultPageValues(), id: "all" } });

        const response = await this.props.getAttendance(this.props.match.params.eventId, [], pageNumber, filters);

        if (response)
            this.setState({ all: { ...response, isFetching: false } });
        else
            this.setState({ all: { ...this.state.all, hasError: true, isFetching: false } });
    }

    private fetchAttending = async (pageNumber: number, filters: Partial<AttendanceFilterValues>): Promise<void> => {
        this.setState({ attending: { ...this.getDefaultPageValues(), id: "attending" } });

        const response = await this.props.getAttendance(this.props.match.params.eventId, ["InPerson", "Online"], pageNumber, filters);

        if (response)
            this.setState({ attending: { ...response, isFetching: false } });
        else
            this.setState({ attending: { ...this.state.attending, hasError: true, isFetching: false } });
    }

    private fetchNotAttending = async (pageNumber: number, filters: Partial<AttendanceFilterValues>): Promise<void> => {
        this.setState({ notAttending: { ...this.getDefaultPageValues(), id: "notAttending" } });

        const response = await this.props.getAttendance(this.props.match.params.eventId, ["NotAttending"], pageNumber, filters);

        if (response)
            this.setState({ notAttending: { ...response, isFetching: false } });
        else
            this.setState({ notAttending: { ...this.state.notAttending, hasError: true, isFetching: false } });
    }

    private fetchUndecided = async (pageNumber: number, filters: Partial<AttendanceFilterValues>): Promise<void> => {
        this.setState({ undecided: { ...this.getDefaultPageValues(), id: "undecided" } });

        const response = await this.props.getAttendance(this.props.match.params.eventId, ["Undecided"], pageNumber, filters);

        if (response)
            this.setState({ undecided: { ...response, isFetching: false } });
        else
            this.setState({ undecided: { ...this.state.undecided, hasError: true, isFetching: false } });
    }

    private fetchWaitlist = async (pageNumber: number, filters: Partial<AttendanceFilterValues>): Promise<void> => {
        this.setState({ waitlist: { ...this.getDefaultPageValues(), id: "waitlist" } });

        const response = await this.props.getAttendance(this.props.match.params.eventId, ["Waitlist"], pageNumber, filters);

        if (response)
            this.setState({ waitlist: { ...response, isFetching: false } });
        else
            this.setState({ waitlist: { ...this.state.waitlist, hasError: true, isFetching: false } });
    }

    private getDefaultPageValues = (): AttendanceListingPage => {
        return {
            id: "",
            attendees: [],
            currentPage: 0,
            eventEndTime: "",
            eventStartTime: "",
            eventType: "standard",
            questions: {},
            title: "",
            totalAttendees: 0,
            totalPages: 0,
            hasError: false,
            isFetching: true
        };
    }

    private getEventTitle = (): string => {
        return this.state.all.title || this.state.attending.title || this.state.notAttending.title || this.state.undecided.title || this.state.waitlist.title || "";
    }

    private getTabLabel = (label: string, page: AttendanceListingPage): string => {
        if (!!page.currentPage)
            return `${label} (${page.totalAttendees})`;
        return label;
    }

    private onSelectTab = (tabIndex: number) => {
        this.setState({ ...this.state, selectedTab: tabIndex });
    }
}

interface RouteParams {
    tenant: string;
    eventId: string;
}

interface ComponentProps {
}

interface ComponentState {
    all: AttendanceListingPage;
    attending: AttendanceListingPage;
    notAttending: AttendanceListingPage;
    undecided: AttendanceListingPage;
    waitlist: AttendanceListingPage;

    selectedItems: Attendee[];
    selectedTab: ATTENDANCE_LISTING_TAB;
    showFilters: boolean;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps & RouteComponentProps<RouteParams>) => ({
        ...ownProps,
        errorMessage: state.events.errorMessage,
        isFetching: state.events.isFetching,
        shouldFetch: state.events.shouldFetch,
        tenant: state.tenant.id
    }),
    {
        clearErrorMessage: actions.clearErrorMessage,
        getAttendance: actions.getAttendance,
        getAttendanceCSV: actions.getAttendanceCSV,
        redirectTo: push
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(AttendanceListing);
